import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, History, LayoutGrid, List, PackageSearch, Search, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useCart } from "../../../context/CartContext";
import { todayAthens } from "../../../lib/format";
import { useCategories, useLastBought, usePromotions, useShop } from "../hooks";
import { promoByItem } from "../promoTrack";
import { ProductCard, ProductGridSkeleton } from "../ProductCard";
import { ProductRow } from "../ProductRow";
import { EmptyState, ErrorNote } from "../../../components/common/EmptyState";

const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const chip = (on) => `h-10 shrink-0 rounded-full px-4 text-sm font-medium transition-colors duration-150 ${on ? "bg-navy text-white" : "bg-white text-navy hover:bg-slate-100"}`;

const PERIODS = [["3", "shop2.filter3"], ["6", "shop.filter6"], ["12", "shop.filter12"], ["all", "shop2.filterAllTime"]];
const HIDDEN_CATS = ["G999", "G998"]; // Έξοδα/Τέλη, Διάφορα Είδη — not orderable from the shop
// Business units in the order the sales team uses; the rest alphabetically
const CAT_ORDER = ["BU:Χαρτικά", "BU:Καθαριστικά", "BU:Συσκευασία", "BU:Εργαλεία Καθαρισμού", "BU:Απορρίματα", "BU:Hotel Amenities", "BU:Air Care", "BU:Εξοπλισμός", "BU:Αναλώσιμα", "BU:Πισίνα"];
const catRank = (code) => { const i = CAT_ORDER.indexOf(code); return i === -1 ? CAT_ORDER.length : i; };

const PeriodSelect = ({ period, setPeriod }) => {
  const { t } = useI18n();
  return (
    <label className={`flex h-12 shrink-0 items-center gap-1.5 rounded-full px-3 shadow-sm ${period === "all" ? "bg-white text-navy" : "bg-navy text-white"}`}>
      <History className="h-4 w-4" />
      <select value={period} onChange={(e) => setPeriod(e.target.value)} className="max-w-[6rem] bg-transparent text-sm font-medium outline-none sm:max-w-[9rem]" data-testid="period-filter">
        {PERIODS.map(([v, k]) => <option key={v} value={v} className="text-navy" data-testid={`period-${v}`}>{t(k)}</option>)}
      </select>
    </label>
  );
};

// cat = { bu, group, family }: one drill-down row (business unit > group > category) instead of stacked rows
const Filters = ({ cat, setCat, categories }) => {
  const { t, lang } = useI18n();
  const hasVisible = (c) => c.parent_code !== null || categories.some((g) => g.parent_code === c.code && !HIDDEN_CATS.includes(g.code));
  const kids = (p) => categories.filter((c) => (c.parent_code || null) === p && !HIDDEN_CATS.includes(c.code) && hasVisible(c))
    .sort((a, b) => p === null ? catRank(a.code) - catRank(b.code) : 0);
  const name = (code) => categories.find((c) => c.code === code)?.[`name_${lang}`] || code;
  const deep = cat.group && kids(cat.group).length > 0; // leaf sub-categories keep their siblings visible
  const back = deep ? { label: name(cat.group), to: { bu: cat.bu } } : cat.bu ? { label: name(cat.bu), to: {} } : null;
  const level = deep ? kids(cat.group) : cat.bu ? kids(cat.bu) : kids(null);
  const value = deep ? cat.family : cat.bu ? cat.group : cat.bu;
  const pick = (code) => setCat(deep ? { ...cat, family: code } : cat.bu ? { bu: cat.bu, group: code } : { bu: code });
  const row = useRef(null);
  useEffect(() => {
    row.current?.scrollTo({ left: 0 });
  }, [cat.bu, cat.group]);
  return (
    <div ref={row} className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none]" data-testid="category-chips">
      {back ? (
        <button type="button" className={`${chip(true)} flex items-center gap-1 pl-2`} onClick={() => setCat(back.to)} data-testid="category-back"><ChevronLeft className="h-4 w-4" />{back.label}</button>
      ) : (
        <button type="button" className={chip(!cat.bu)} onClick={() => setCat({})} data-testid="category-chips-all">{t("shop.allCategories")}</button>
      )}
      {level.map((c) => <button key={c.code} type="button" className={chip(value === c.code)} onClick={() => pick(value === c.code ? "" : c.code)} data-testid={`category-${c.code}`}>{c[`name_${lang}`]}</button>)}
    </div>
  );
};

const ViewSwitch = ({ view, setView }) => {
  const { t } = useI18n();
  const btn = (on) => `grid h-9 w-10 place-items-center rounded-full transition-colors ${on ? "bg-navy text-white" : "text-navy hover:bg-slate-100"}`;
  return (
    <div className="flex rounded-full bg-white p-0.5 shadow-sm" role="group" aria-label={t("x.view.label")} data-testid="view-switch">
      <button type="button" className={btn(view === "grid")} onClick={() => setView("grid")} aria-pressed={view === "grid"} aria-label={t("x.view.grid")} data-testid="view-grid"><LayoutGrid className="h-4 w-4" /></button>
      <button type="button" className={btn(view === "list")} onClick={() => setView("list")} aria-pressed={view === "list"} aria-label={t("x.view.list")} data-testid="view-list"><List className="h-4 w-4" /></button>
    </div>
  );
};

const AddAllPeriod = ({ list, last }) => {
  const { t } = useI18n();
  const { addMany, qtyOf } = useCart();
  const rows = list.filter((i) => !qtyOf(i.code) && last?.[i.code]).map((i) => ({ item_code: i.code, qty_boxes: last[i.code].qty_boxes || 1, uom: last[i.code].uom || "piece" }));
  if (!rows.length) return null;
  return (
    <button type="button" onClick={() => { addMany(rows); toast.success(t("shop.addedAll", { n: rows.length })); }}
      className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand text-sm font-semibold text-navy shadow-sm sm:w-auto sm:px-5" data-testid="add-all-period">
      <ShoppingBasket className="h-4 w-4" /> {t("shop.addAllPeriod", { n: rows.length })}
    </button>
  );
};

export default function Products() {
  const { t, lang } = useI18n();
  const shop = useShop();
  const cats = useCategories();
  const last = useLastBought();
  const promos = usePromotions();
  const promoMap = useMemo(() => promoByItem(promos.data), [promos.data]);
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState("all");
  const [cat, setCatRaw] = useState({});
  const [shown, setShown] = useState(60);
  const setCat = (c) => { setCatRaw(c); setShown(60); };
  const [view, setView] = useState(() => localStorage.getItem("vavlis.productsView") || "grid");
  useEffect(() => {
    localStorage.setItem("vavlis.productsView", view);
  }, [view]);

  const list = useMemo(() => {
    const lb = last.data || {};
    const since = period === "all" ? null : todayAthens(-Number(period) * 30.5);
    const nq = norm(q.trim());
    return shop.sellable.filter((i) => !HIDDEN_CATS.includes(i.category) && (!cat.bu || `BU:${i.family || "Λοιπά"}` === cat.bu) && (!cat.group || i.category === cat.group)
      && (!cat.family || `${i.category}:${i.subcategory}` === cat.family)
      && (!since || (lb[i.code] && lb[i.code].date >= since))
      && (!nq || norm(`${i.code} ${i.name_el} ${i.name_en} ${i.brand} ${i.barcode}`).includes(nq)))
      .sort((a, b) => a[`name_${lang}`].localeCompare(b[`name_${lang}`], lang));
  }, [shop.sellable, last.data, period, cat, q, lang]);

  return (
    <div className="space-y-4" data-testid="products-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-navy sm:text-3xl">{t("shop.nav.products")}</h1>
        <ViewSwitch view={view} setView={setView} />
      </div>
      <div className="sticky top-16 z-20 -mx-4 space-y-2 bg-canvas/95 px-4 py-2 backdrop-blur">
        <div className="flex gap-2">
          <label className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-full bg-white px-4 shadow-sm focus-within:ring-4 focus-within:ring-brand/20">
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input type="search" name="product-search" autoComplete="off" autoCorrect="off" spellCheck={false} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("shop.searchPh")} data-testid="product-search-input" className="h-full min-w-0 flex-1 bg-transparent text-base text-navy outline-none placeholder:text-slate-400" />
          </label>
          <PeriodSelect period={period} setPeriod={setPeriod} />
        </div>
        <Filters cat={cat} setCat={setCat} categories={(cats.data || []).filter((c) => c.code !== "CUSTOM" || shop.sellable.some((i) => i.category === "CUSTOM"))} />
      </div>
      {shop.isError && <ErrorNote text={shop.priceError?.response?.status === 404 ? t("shop.noCustomerLink") : t("common.error")} testId="products-error" />}
      {period !== "all" && !shop.isLoading && <AddAllPeriod list={list} last={last.data} />}
      {shop.isLoading ? <ProductGridSkeleton /> : shop.isError ? null : list.length === 0 ? (
        <EmptyState icon={PackageSearch} title={t("shop.noResults")} sub={t("shop.noResultsSub")} testId="products-empty"
          action={<button type="button" onClick={() => { setQ(""); setCat({}); setPeriod("all"); }} className="h-11 rounded-full bg-navy px-5 text-sm font-semibold text-white" data-testid="products-reset">{t("shop.filterAll")}</button>} />
      ) : (
        <>
          {view === "list" ? (
            <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl bg-white shadow-sm" data-testid="products-list">
              {list.slice(0, shown).map((i) => <ProductRow key={i.code} item={i} price={shop.priceMap[i.code]} last={last.data?.[i.code]} promo={promoMap[i.code]} />)}
            </ul>
          ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6" data-testid="products-grid">
            {list.slice(0, shown).map((i) => <ProductCard key={i.code} item={i} price={shop.priceMap[i.code]} last={last.data?.[i.code]} promo={promoMap[i.code]} />)}
          </div>
          )}
          {list.length > shown && (
            <button type="button" onClick={() => setShown((n) => n + (view === "list" ? 150 : 60))} className="mx-auto flex h-12 items-center rounded-full bg-white px-6 text-sm font-semibold text-navy shadow-sm" data-testid="products-more">
              {t("shop.showMoreN", { n: list.length - shown })}
            </button>
          )}
        </>
      )}
    </div>
  );
}
